import { z } from "zod";

/**
 * 全部错误码。服务端只返回 code + params，文案由客户端按 locale 翻译
 * （packages/i18n 的 `error.<code>` key，CI 校验一一对应）。
 * 新增错误码：在这里加一行 + 补 zh-CN / en 翻译。
 */
export const ERROR_CODES = {
  // 通用
  "common.bad_request": 400,
  "common.validation_failed": 400,
  "common.unauthenticated": 401,
  "common.forbidden": 403,
  "common.not_found": 404,
  "common.conflict": 409,
  "common.precondition_failed": 412,
  "common.rate_limited": 429,
  "common.internal": 500,
  "common.unavailable": 503,
  // 鉴权
  "auth.invalid_credentials": 401,
  "auth.email_taken": 409,
  "auth.refresh_invalid": 401,
  "auth.registration_closed": 403,
  // 租户
  "tenant.member_exists": 409,
  "tenant.last_owner": 409,
  // 凭据
  "credential.invalid": 400,
  "credential.verify_failed": 422,
  "credential.in_use": 409,
  "credential.not_allowed": 403,
  // agent
  "agent.not_running": 409,
  "agent.busy": 409,
  "agent.budget_exhausted": 402,
  "agent.unreachable": 503,
  "agent.provision_failed": 500,
  // 文件
  "fs.path_invalid": 400,
  "fs.not_found": 404,
  "fs.too_large": 413,
  "fs.is_directory": 400,
  "pty.limit_reached": 429,
  // 审批
  "approval.already_resolved": 409,
  // 节点
  "node.none_available": 503,
  "node.join_token_invalid": 401,
  // 会话 / 护栏
  "conversation.paused": 409,
  "conversation.hop_limit": 429,
  "conversation.budget_exhausted": 402,
  "conversation.loop_detected": 429,
  // P2 协作
  "task.invalid_transition": 409,
  "question.already_answered": 409,
  "git.unavailable": 503,
  "git.not_mergeable": 409,
  "mcp.scope_denied": 403,
  "mcp.unknown_teammate": 404,
  // P4 媒体
  "media.provider_unavailable": 503,
  "media.job_failed": 502,
  /** 供应商不支持该操作，或源资产不满足条件（例如 animate 需要同一供应商绑骨的结果） */
  "media.op_unsupported": 422,
  // P5 会议
  "meeting.invalid_state": 409,
  "meeting.not_your_turn": 409,
  // P6 计费 / 市场
  "billing.insufficient_balance": 402,
  "billing.provider_unavailable": 503,
  "market.listing_unavailable": 404,
  // P7 能力包
  "capability.not_found": 404,
  "capability.invalid": 400,
  "capability.id_taken": 409,
  "capability.image_conflict": 409,
  "secret.not_found": 404,
  "secret.in_use": 409,
  "mcp.server_in_use": 409,
  "mcp.server_unreachable": 502,
  "market.not_rented": 403,
  "market.rental_expired": 409,
} as const satisfies Record<string, number>;

export type ErrorCode = keyof typeof ERROR_CODES;
export const ErrorCodeSchema = z.enum(Object.keys(ERROR_CODES) as [ErrorCode, ...ErrorCode[]]);

export const ApiErrorBody = z.object({
  code: ErrorCodeSchema,
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
  requestId: z.string().optional(),
  /** 仅 validation_failed：字段路径 → 子错误码 */
  fields: z.record(z.string(), z.string()).optional(),
});
export type ApiErrorBody = z.infer<typeof ApiErrorBody>;

export const ApiErrorResponse = z.object({ error: ApiErrorBody });
export type ApiErrorResponse = z.infer<typeof ApiErrorResponse>;

export class ApiError extends Error {
  readonly status: number;
  constructor(
    readonly code: ErrorCode,
    readonly params?: ApiErrorBody["params"],
    readonly fields?: ApiErrorBody["fields"],
  ) {
    super(code);
    this.status = ERROR_CODES[code];
  }
  toBody(requestId?: string): ApiErrorResponse {
    return { error: { code: this.code, params: this.params, fields: this.fields, requestId } };
  }
}
